import React, { useState, useEffect } from 'react';
import Box from '../Atoms/Box';
import Typography from '../Atoms/Typography';
import Button from '../Atoms/Button/Button';
import Dialog, { DialogContent } from '../Atoms/Dialog/Dialog';
import CustomCheckbox from './CustomCheckbox';

interface AvailableColumn {
  key: string;
  label: string;
}

interface AlgoliaTableColumnsProps {
  isOpen: boolean;
  onClose: () => void;
  availableColumns: AvailableColumn[];
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
}

/**
 * AlgoliaTableColumns component
 * Dialog for configuring which columns to display in the Algolia search results table
 */
const AlgoliaTableColumns: React.FC<AlgoliaTableColumnsProps> = ({
  isOpen,
  onClose,
  availableColumns,
  selectedColumns,
  onColumnsChange,
}) => {
  const [tempSelectedColumns, setTempSelectedColumns] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Initialize tempSelectedColumns when component opens
  useEffect(() => {
    if (isOpen) {
      setTempSelectedColumns([...selectedColumns]);
      setHasChanges(false);
    }

    // Cleanup when dialog closes
    return () => {
      if (!isOpen) {
        setHasChanges(false);
      }
    };
  }, [isOpen, selectedColumns]);

  const handleColumnToggle = (columnKey: string) => {
    setTempSelectedColumns((prev) => {
      const newColumns = prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey)
        : [...prev, columnKey];
      setHasChanges(JSON.stringify(newColumns) !== JSON.stringify(selectedColumns));
      return newColumns;
    });
  };

  const handleApply = () => {
    try {
      onColumnsChange(tempSelectedColumns);
      onClose();
    } catch (error) {
      // Continue with dialog open if there's an error
    }
  };

  const handleCancel = () => {
    setTempSelectedColumns([...selectedColumns]);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      title="Configure Columns"
      showCloseIcon
      showActionButtons={false}
      aria-labelledby="column-configuration-title"
    >
      <DialogContent>
        <Typography
          variant="body2"
          sx={{ mb: 2 }}
          data-testid="column-configuration-description"
        >
          Select the columns you want to display in the table.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: '300px',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
          role="group"
          aria-label="Column selection options"
        >
          {availableColumns.map(({ key, label }) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                gap: 1,
              }}
            >
              <CustomCheckbox
                checked={tempSelectedColumns.includes(key)}
                onChange={() => handleColumnToggle(key)}
                id={`column-${key}`}
                checkboxSize="large"
                ariaLabel={`Show ${label} column`}
              />
              <label htmlFor={`column-${key}`} style={{ cursor: 'pointer' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                  }}
                >
                  {label}
                </Typography>
              </label>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            aria-label="Cancel column changes"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!hasChanges}
            aria-label="Apply column changes"
          >
            Apply
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AlgoliaTableColumns;
